import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShieldAlert, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader } from '@/components/common/Loader';
import { authApi } from '@/api/endpoints';
import { toast } from '@/store/toast';

export default function IpChangePage() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState<{ maskedEmail: string; pendingIp: string; expiresAtUtc: string; trigger: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setErr(t('ipChange.invalidToken')); setLoading(false); return; }
    authApi.getIpChange(token)
      .then(setInfo)
      .catch(() => setErr(t('ipChange.invalidLink')))
      .finally(() => setLoading(false));
  }, [token, t]);

  async function submit() {
    if (!token || !password) return;
    setBusy(true);
    try {
      await authApi.confirmIpChange(token, password);
      setDone(true);
      toast.success(t('ipChange.success'), t('ipChange.successHint'));
      setTimeout(() => navigate('/login', { replace: true }), 1800);
    } catch (e: any) {
      toast.error(t('ipChange.failed'), e?.response?.data?.reason || e?.message || t('ipChange.failedHint'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="w-full max-w-md bg-card border rounded-lg shadow-sm p-8">
        <div className="flex items-center gap-2 mb-6 text-destructive">
          <ShieldAlert className="h-6 w-6" />
          <h1 className="text-xl font-semibold">{t('ipChange.title')}</h1>
        </div>

        {loading && <div className="flex items-center justify-center py-12"><Loader /></div>}

        {!loading && err && (
          <div className="bg-destructive/10 border border-destructive/40 rounded p-3 text-sm flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
            <div>{err}</div>
          </div>
        )}

        {!loading && info && !done && (
          <>
            <div className="text-sm text-muted-foreground mb-4">
              {t('ipChange.intro', { email: info.maskedEmail })}
            </div>
            <div className="bg-muted/50 rounded p-3 mb-4 text-sm space-y-1">
              <div><span className="text-muted-foreground">{t('ipChange.pendingIp')}</span> <span className="font-mono font-medium">{info.pendingIp}</span></div>
              <div><span className="text-muted-foreground">{t('ipChange.origin')}</span> {info.trigger === 'mid_session' ? t('ipChange.originMidSession') : t('ipChange.originLogin')}</div>
              <div><span className="text-muted-foreground">{t('ipChange.expiresAt')}</span> {new Date(info.expiresAtUtc).toLocaleString()}</div>
            </div>
            <div className="text-xs text-muted-foreground mb-4">{t('ipChange.hint')}</div>
            <Label htmlFor="password" className="text-xs">{t('ipChange.passwordLabel')}</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && password) submit(); }}
              placeholder={t('ipChange.passwordPlaceholder')}
              autoFocus
              className="mb-4"
            />
            <Button onClick={submit} disabled={!password || busy} className="w-full">
              {busy ? t('ipChange.submitting') : t('ipChange.submit')}
            </Button>
            <div className="mt-4 text-xs text-destructive bg-destructive/5 border border-destructive/30 rounded p-2">
              <strong>{t('ipChange.notYou')}</strong> {t('ipChange.notYouHint')}
            </div>
          </>
        )}

        {!loading && done && (
          <div className="text-center py-6">
            <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto mb-3" />
            <div className="font-medium">{t('ipChange.success')}</div>
            <div className="text-sm text-muted-foreground mt-1">{t('ipChange.successRedirect')}</div>
          </div>
        )}
      </div>
    </div>
  );
}
